/**
 * Analytics & Tracing Module
 *
 * Provides structured logging and event tracking that can be migrated to Amplitude.
 * Currently logs to console in development, but the interface is designed to
 * seamlessly integrate with Amplitude or other analytics providers.
 *
 * Usage:
 *   import { analytics } from '../lib/analytics';
 *   analytics.track('button_clicked', { buttonId: 'submit', page: 'login' });
 *   analytics.page('Dashboard');
 *   analytics.error('api_error', error, { endpoint: '/api/health' });
 */

type EventProperties = Record<string, string | number | boolean | null | undefined>;

interface AnalyticsUser {
  userId?: string;
  email?: string;
  name?: string;
  clientId?: string;
  clientName?: string;
  role?: string;
  isSuperAdmin?: boolean;
}

interface AnalyticsConfig {
  enabled: boolean;
  debug: boolean;
  environment: string;
  amplitudeApiKey?: string;
  sessionId?: string;
}

// Session ID for correlating events
const sessionId = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

// Default config
const config: AnalyticsConfig = {
  enabled: true,
  debug: import.meta.env.DEV || localStorage.getItem("analytics_debug") === "true",
  environment: import.meta.env.MODE || "development",
  sessionId,
};

// Current user context
let currentUser: AnalyticsUser | null = null;

/**
 * Format timestamp for console output
 */
function formatTime(): string {
  return new Date().toISOString().slice(11, 23);
}

/**
 * Log to console with consistent formatting
 */
function consoleLog(
  level: "info" | "warn" | "error" | "debug",
  category: string,
  message: string,
  properties?: EventProperties
): void {
  if (!config.debug && level === "debug") return;

  const prefix = `[${formatTime()}] [${category}]`;
  const propsStr = properties ? ` ${JSON.stringify(properties)}` : "";

  switch (level) {
    case "error":
      console.error(`${prefix} ${message}${propsStr}`);
      break;
    case "warn":
      console.warn(`${prefix} ${message}${propsStr}`);
      break;
    case "debug":
      console.debug(`${prefix} ${message}${propsStr}`);
      break;
    default:
      console.info(`${prefix} ${message}${propsStr}`);
  }
}

/**
 * Build event payload compatible with Amplitude format
 */
function buildEventPayload(eventType: string, properties?: EventProperties) {
  return {
    event_type: eventType,
    time: Date.now(),
    session_id: config.sessionId,
    user_id: currentUser?.userId,
    user_properties: currentUser
      ? {
          email: currentUser.email,
          name: currentUser.name,
          client_id: currentUser.clientId,
          client_name: currentUser.clientName,
          role: currentUser.role,
          is_super_admin: currentUser.isSuperAdmin,
        }
      : undefined,
    event_properties: {
      environment: config.environment,
      ...properties,
    },
  };
}

/**
 * Send event to Amplitude (when configured)
 * Errors are caught and logged to prevent analytics from breaking the app
 */
async function sendToAmplitude(payload: ReturnType<typeof buildEventPayload>): Promise<void> {
  if (!config.amplitudeApiKey) return;

  try {
    const response = await fetch("https://api2.amplitude.com/2/httpapi", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "*/*",
      },
      body: JSON.stringify({
        api_key: config.amplitudeApiKey,
        events: [payload],
      }),
    });

    if (!response.ok) {
      // Log error but don't throw - analytics should never break the app
      if (config.debug) {
        const errorText = await response.text().catch(() => "Unknown error");
        consoleLog("warn", "Analytics", `Amplitude API error: ${response.status}`, {
          status: response.status,
          error: errorText,
        });
      }
    }
  } catch (error: unknown) {
    // Silently fail - analytics should never break the app
    if (config.debug) {
      consoleLog("warn", "Analytics", "Failed to send to Amplitude", {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }
}

/**
 * Analytics API
 */
export const analytics = {
  /**
   * Configure analytics settings
   */
  configure(options: Partial<AnalyticsConfig>): void {
    Object.assign(config, options);
    consoleLog("debug", "Analytics", "Configured", options);
  },

  /**
   * Set the current user context
   */
  identify(user: AnalyticsUser | null): void {
    currentUser = user;
    if (user) {
      consoleLog("info", "Analytics", `Identified user: ${user.email || user.userId}`, {
        clientId: user.clientId,
        role: user.role,
      });
    } else {
      consoleLog("info", "Analytics", "User cleared");
    }
  },

  /**
   * Track a custom event
   */
  track(eventName: string, properties?: EventProperties): void {
    if (!config.enabled) return;

    const payload = buildEventPayload(eventName, properties);
    consoleLog("info", "Event", eventName, properties);
    sendToAmplitude(payload);
  },

  /**
   * Track a page view
   */
  page(pageName: string, properties?: EventProperties): void {
    if (!config.enabled) return;

    const payload = buildEventPayload("page_view", {
      page_name: pageName,
      url: window.location.href,
      path: window.location.pathname,
      referrer: document.referrer,
      ...properties,
    });
    consoleLog("info", "Page", pageName, { path: window.location.pathname });
    sendToAmplitude(payload);
  },

  /**
   * Track an error
   */
  error(errorType: string, error: Error | unknown, properties?: EventProperties): void {
    if (!config.enabled) return;

    const errorMessage = error instanceof Error ? error.message : String(error);
    const errorStack = error instanceof Error ? error.stack : undefined;

    const payload = buildEventPayload("error", {
      error_type: errorType,
      error_message: errorMessage,
      error_stack: errorStack?.slice(0, 500), // Limit stack trace length
      ...properties,
    });
    consoleLog("error", "Error", `${errorType}: ${errorMessage}`, properties);
    sendToAmplitude(payload);
  },

  /**
   * Track an API request (timing, status)
   */
  apiRequest(
    endpoint: string,
    method: string,
    status: number,
    durationMs: number,
    properties?: EventProperties
  ): void {
    if (!config.enabled) return;

    const isError = status >= 400;
    const payload = buildEventPayload("api_request", {
      endpoint,
      method,
      status,
      duration_ms: durationMs,
      is_error: isError,
      ...properties,
    });

    if (isError) {
      consoleLog("warn", "API", `${method} ${endpoint} - ${status} (${durationMs}ms)`, properties);
    } else if (config.debug) {
      consoleLog("debug", "API", `${method} ${endpoint} - ${status} (${durationMs}ms)`);
    }

    sendToAmplitude(payload);
  },

  /**
   * Track a user action (click, input, etc.)
   */
  action(actionName: string, target: string, properties?: EventProperties): void {
    if (!config.enabled) return;

    const payload = buildEventPayload("user_action", {
      action: actionName,
      target,
      ...properties,
    });
    consoleLog("debug", "Action", `${actionName} on ${target}`, properties);
    sendToAmplitude(payload);
  },

  /**
   * Get current session ID
   */
  getSessionId(): string {
    return sessionId;
  },

  /**
   * Get current config (for debugging)
   */
  getConfig(): AnalyticsConfig {
    return { ...config };
  },
};

/**
 * Enable debug mode via console
 * Usage: window.enableAnalyticsDebug()
 */
if (typeof window !== "undefined") {
  (window as { enableAnalyticsDebug?: () => void }).enableAnalyticsDebug = () => {
    localStorage.setItem("analytics_debug", "true");
    config.debug = true;
    console.info("[Analytics] Debug mode enabled. Reload to see all events.");
  };

  (window as { disableAnalyticsDebug?: () => void }).disableAnalyticsDebug = () => {
    localStorage.removeItem("analytics_debug");
    config.debug = false;
    console.info("[Analytics] Debug mode disabled.");
  };
}

export default analytics;
